<script setup lang="ts">
import { DeliveryMethods } from '@/api/modules/basket/types'
import type { Cost } from '@/types/api/api-v3/basket'
import type { BasketPoint } from '@/types/api/api-v3/basket-points'
import { isAddressClient } from '@/types/store/basket/index'

import type { AddressClient } from '@/api/modules/addresses/addresses.types'

const BasketAddressesPointItem = defineAsyncComponent(
  () =>
    import('@/components/Basket/basket-addresses/BasketAddressesPointItem.vue'),
)
const BasketAddressesClientItem = defineAsyncComponent(
  () =>
    import(
      '@/components/Basket/basket-addresses/BasketAddressesClientItem.vue'
    ),
)

interface ComputedAddresses {
  data: AddressClient | BasketPoint
  disabled: boolean
  selected: boolean
}

type Props = {
  maxCountOfVisibleAddresses: number
  addresses: AddressClient[] | BasketPoint[]
  showAllAddresses: boolean
  deliveryMethod: DeliveryMethods
  activeAddress?: AddressClient | BasketPoint
  deliveryCost?: Cost | null
}

const props = defineProps<Props>()

const emit = defineEmits([
  'change',
  'click-radio',
  'open-edit-address-dialog',
  'open-delete-address-dialog',
])

const countOfVisibleAddresses = computed(() =>
  props.showAllAddresses
    ? props.addresses.length
    : props.maxCountOfVisibleAddresses,
)

const addressesList = computed(() => {
  const list: ComputedAddresses[] = []

  props.addresses.forEach((address) => {
    const isDisabled = isAddressClient(address) && !address?.is_in_delivery_area
    const isSelected = !!(
      props.activeAddress && address.id === props.activeAddress.id
    )

    list.push({
      data: address,
      disabled: isDisabled,
      selected: isSelected,
    })
  })

  return list
})

const onClickRadio = (event: Event, address: AddressClient | BasketPoint) => {
  emit('click-radio', { event, address })
}

const onChange = (addressId: number) => {
  const selectedAddress = addressesList.value.find(
    (address) => address.data.id === addressId,
  )
  emit('change', selectedAddress?.data)
}
</script>

<template>
  <the-radio-group
    class="radio-group"
    v-bind="$attrs"
    name="basketAddresses"
    :model-value="activeAddress?.id || ''"
    @update:model-value="onChange"
  >
    <the-radio
      v-for="(address, index) in addressesList"
      v-show="index + 1 <= countOfVisibleAddresses"
      :key="address.data.id"
      :value="address.data.id"
      @click="onClickRadio($event, address.data)"
    >
      <basket-addresses-point-item
        v-if="deliveryMethod != DeliveryMethods.COURIER"
        :type="deliveryMethod"
        :point="(address.data as BasketPoint)"
      />

      <basket-addresses-client-item
        v-else-if="deliveryMethod === DeliveryMethods.COURIER"
        :address="address.data as AddressClient"
        :selected="address.selected"
        :disabled="address.disabled"
        :delivery-cost="deliveryCost"
        @open-edit-address-dialog="emit('open-edit-address-dialog', $event)"
        @open-delete-address-dialog="emit('open-delete-address-dialog', $event)"
      />
    </the-radio>
  </the-radio-group>
</template>
